import {Controls, ControlsItem} from '@genestack/ui';
import React from 'react';
import {useLocation} from 'wouter';

import {DocumentTypeLabel} from '../../../../../components/document-type-label';
import {EditableTitleApi} from '../../../../../components/editable-title';
import {useDocRevisionsContext, CommitDto} from '../../../../../providers/version';
import {isDeletedDocument, isDocumentBeingEdited} from '../../../../../utils/document';
import {VersionLabel} from '../../../common/version-history/version-label';
import {useRenameWorkingCopyMutation} from '../../hooks/use-rename-working-copy-mutation';

import {DocumentControl} from './document-control';
import styles from './document-editor-header.module.css';
import {EditViewEditorMenu} from './edit-view-menu';
import {PublishDialog} from './publish-dialog';
import {useStartEditingDocument} from './use-start-editing-document';
import {EditorHeader} from '../../../../../components/editor/editor-header/editor-header';
import {DocumentEditStatusIcon} from '../../doc-status-icon/document-status-icon';
import {TagsList} from '../../../common/tags/tags-list';
import {OntologyElement} from '../../../common/ontology-view/requests/use-ontology-element-query';

interface Props {
    document: EditModeDoc;
    workingCopyName?: string;
    lastAction?: CommitDto;
    onPublishSubmit?: (commitMessage?: string) => void;
    ontologyElementInfo?: OntologyElement;
    isRevision: boolean;
}

export function useDocumentTitle(document: EditModeDoc, workingCopyName?: string) {
    const {selectedRevision} = useDocRevisionsContext();

    if (selectedRevision) {
        return selectedRevision.documentName;
    }

    if (isDocumentBeingEdited(document) && workingCopyName) {
        return workingCopyName;
    }

    return document.name;
}

export function DocumentEditorHeader(props: Props) {
    const [, navigate] = useLocation();

    const titleRef = React.useRef<EditableTitleApi>(null);
    const [openDialog, setOpenDialog] = React.useState(false);

    const {selectedRevision} = useDocRevisionsContext();
    const renameWorkingCopy = useRenameWorkingCopyMutation();

    const handleClickRename = () => {
        titleRef.current?.rename();
    };

    const handleRename = (name: string) => {
        renameWorkingCopy.mutate({
            accession: props.document.accession,
            type: props.document.type,
            name
        });
    };

    const handleOpenPublishDialog = () => {
        setOpenDialog(true);
    };

    const handleClosePublishDialog = () => {
        setOpenDialog(false);
    };

    const documentTitle = useDocumentTitle(props.document, props.workingCopyName);

    const {startEditing, isDocEditable, editingDisabledReason} = useStartEditingDocument({
        document: props.document,
        revisionId: selectedRevision?.revision
    });

    const onCopyToNewDraft = async () => {
        await startEditing();

        navigate(`/${props.document.accession}`);
    };

    const creatingWorkingCopyDisabledReason = (function () {
        if (!isDocEditable) {
            return editingDisabledReason;
        }

        if (isDeletedDocument(props.document)) {
            return 'Document is in the Recycle bin. Restore it to update its content.';
        }

        if (isDocumentBeingEdited(props.document)) {
            return 'Cannot copy if not published draft is present. Publish or discard draft first.';
        }

        return undefined;
    })();

    return (
        <>
            <EditorHeader
                ref={titleRef}
                data-qa="doc-header"
                className={
                    (!props.isRevision && isDeletedDocument(props.document)) ||
                    (selectedRevision && selectedRevision.action === 'TRASH_BIN')
                        ? styles.warning
                        : undefined
                }
                disableEditingTitle={!isDocumentBeingEdited(props.document)}
                titleProps={{
                    append: (
                        <DocumentEditStatusIcon
                            document={props.document}
                            showDeleted={false}
                            showReadOnly={false}
                        />
                    ),
                    'data-qa': 'doc-title'
                }}
                name={documentTitle}
                onRename={handleRename}
                menu={
                    props.isRevision ? null : (
                        <EditViewEditorMenu
                            data-qa="doc-header-menu-handler"
                            document={props.document}
                            lastAction={props.lastAction}
                            onClickPublish={handleOpenPublishDialog}
                            onClickRename={handleClickRename}
                        />
                    )
                }
                prepend={
                    <DocumentTypeLabel
                        type={props.document.type}
                        readOnly={props.document.readOnly}
                    />
                }
                append={
                    <Controls gap={1}>
                        <ControlsItem>
                            {selectedRevision ? (
                                <VersionLabel
                                    selectedVersion={selectedRevision}
                                    copyToDraftBtnProps={{
                                        onClick: onCopyToNewDraft,
                                        disabledReason: creatingWorkingCopyDisabledReason
                                    }}
                                />
                            ) : null}
                        </ControlsItem>

                        <ControlsItem>
                            <em>{props.ontologyElementInfo?.name}</em>
                        </ControlsItem>
                    </Controls>
                }
                bottomElement={
                    <TagsList documentAccession={props.document.accession} tagsType="both" />
                }
            >
                {!props.isRevision && (
                    <DocumentControl
                        className={styles.documentControl}
                        document={props.document}
                        onClickPublish={handleOpenPublishDialog}
                    />
                )}
            </EditorHeader>

            {props.onPublishSubmit ? (
                <PublishDialog
                    open={openDialog}
                    document={props.document}
                    onSubmit={props.onPublishSubmit}
                    onClose={handleClosePublishDialog}
                />
            ) : null}
        </>
    );
}
